import type { Ref } from 'vue'
// @ts-expect-error virtual file
import { useState } from '#imports'
import { basename } from 'pathe'
import { computed, toValue } from 'vue'
import { $backendFetch } from '../utils/backend'

type LocaleId = number

interface Locale {
  id: LocaleId
  name: string
  code: string
  languageCode: string
  isMain: boolean
}

type TranslatorOption = Record<string, string | number | bigint | Date>
type Translator = (path: string, option?: TranslatorOption) => string

type Translations
  = Record<string, string | Record<string, string> | Array<Record<string, string>>>

const translationsFiles = import.meta.glob<Translations>('~/assets/translations/*.json', {
  eager: false,
  import: 'default',
})

const translationFileByLocaleCode: Record<string, () => Promise<Translations>> = {}

for (const translationsFile in translationsFiles) {
  translationFileByLocaleCode[basename(translationsFile).split('.')[0]]
    = translationsFiles[translationsFile]
}

function get(object: Record<string, any>, path: string, defaultValue?: any): any {
  const paths = path.split('.').map((key) => {
    return key
  })

  let result = object

  for (const key of paths) {
    if (result === undefined || result === null) {
      return defaultValue
    }

    result = result[key]
  }

  return result === undefined ? defaultValue : result
}

export function translate(path: string, option: undefined | TranslatorOption, translations: any): string {
  const prop = get(translations, path, path)
  return String(prop).replace(
    /\{(\w+)\}/g,
    (_, key) => `${option?.[key] ?? `{${key}}`}`,
  )
}

function buildTranslator(translations: Ref<any>): Translator {
  return (path, option) => translate(path, option, toValue(translations))
}

export function useLocale() {
  const ready = useState('_localesReady', () => false)
  const _locales = useState<Locale[]>('_locales', () => [])
  const _localeId = useState<LocaleId>('_localeId')
  const loadedTranslationsLocaleId = useState<LocaleId>('_loadedTranslationsLocaleId')
  const _translations = useState('_translations', () => { })

  async function fetch() {
    const response = await $backendFetch('/enum/locales')
    _locales.value = response.data
    ready.value = true
  }

  const translations = computed((prev) => {
    const _all = _translations.value || {}
    if (Object.keys(_all).length === 0) {
      return prev
    }
    return _all
  })

  const localeId = computed<LocaleId | undefined>((prev) => {
    const currentId = _localeId.value
    if (!currentId) {
      if (prev) {
        return prev
      }

      return 0
    }

    return currentId
  })
  const locales = computed<Locale[]>((prev) => {
    const all = _locales.value ?? []

    if (all.length > 0) {
      return all
    }

    if (all.length === 0 && prev) {
      return prev
    }

    return [
      {
        id: 0,
        name: 'English',
        code: 'en',
        languageCode: 'en',
        isMain: true,
      },
    ]
  })

  const locale = computed<Locale | undefined>(() => {
    const currentLocaleId = localeId.value
    if (currentLocaleId) {
      const found = locales.value.find(l => l.id === currentLocaleId)
      if (found) {
        return found
      }
    }
    return locales.value.find(l => l.isMain)
  })

  function setLocale(locale: Locale | LocaleId) {
    if (typeof locale === 'number') {
      _localeId.value = locale
    }
    else if (typeof locale === 'object') {
      _localeId.value = locale.id
    }
  }

  async function fetchTranslations() {
    const langCode = locale.value?.languageCode
    if (langCode && translationFileByLocaleCode[langCode]) {
      _translations.value = await translationFileByLocaleCode[langCode]()
      loadedTranslationsLocaleId.value = locale.value?.id
    }
  }

  return {
    ready,
    locales,
    locale,
    setLocale,
    fetch,
    t: buildTranslator(translations),
    fetchTranslations,
    loadedTranslationsLocaleId,
  }
}
